import { Controller } from "stimulus"
import { reviewFrame } from "../review-frame/snapshot"
import { stringsFor, suggestLanguage } from "../lib/language-suggestion"

// On the language switcher (_includes/language-switcher.html), on the pages
// with translated copies. When the browser's languages list one the site has
// and not the page's own, a toast at the bottom of the page offers the copy,
// in that language: "Este sitio está disponible en español. Ver en español".
// Only an offer, never a redirect: search engines get the hreflang links,
// and a redirect would fight them (the last translated site lost rankings
// to bad canonicals and redirects).
//
// It appears once per browsing session, a moment after the page, and never
// again in a browser where it was dismissed or where a language was picked,
// from the toast or the switcher. Not in the translation review app's
// frames. The toast is built here rather than in the page, so the Worker and
// the review app see the markup without it, and it is removed before Turbo
// caches the page, so back/forward never restores a dead one.

const DISMISSED = "lang-suggest-dismissed"
const SHOWN = "lang-suggest-shown"
const DELAY = 1200

function read(storage, key) {
  try {
    return window[storage].getItem(key)
  } catch (e) {
    return null
  }
}

function write(storage, key) {
  try {
    window[storage].setItem(key, "1")
  } catch (e) {
    // Storage is blocked (a private window): the toast simply shows again.
  }
}

function track(name) {
  if (window.fathom) window.fathom.trackEvent(name)
}

export default class LanguageSuggestController extends Controller {

  connect() {
    this.element.addEventListener("click", this.onSwitcherClick)
    if (reviewFrame || read("localStorage", DISMISSED) || read("sessionStorage", SHOWN)) return

    const alternates = Array.from(this.element.querySelectorAll("a[hreflang]")).map((a) => ({
      lang: a.getAttribute("hreflang"),
      url: a.getAttribute("href"),
      current: a.getAttribute("aria-current") === "page",
    }))
    const suggestion = suggestLanguage(alternates, navigator.languages || [navigator.language])
    const strings = suggestion && stringsFor(suggestion.lang)
    if (!strings) return

    document.addEventListener("turbo:before-cache", this.removeNow)
    this.timeout = setTimeout(() => this.show(suggestion, strings), DELAY)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onSwitcherClick)
    document.removeEventListener("turbo:before-cache", this.removeNow)
    clearTimeout(this.timeout)
    this.removeNow()
  }

  show(suggestion, strings) {
    write("sessionStorage", SHOWN)

    const toast = document.createElement("div")
    toast.className = "lang-toast"
    toast.setAttribute("role", "status")
    toast.lang = suggestion.lang

    const message = document.createElement("span")
    message.className = "lang-toast-message"
    message.textContent = strings.message

    // A full load, as the switcher's links: the <head> (lang, title,
    // hreflang) has to change with the language.
    const link = document.createElement("a")
    link.className = "lang-toast-link"
    link.href = suggestion.url
    link.hreflang = suggestion.lang
    link.setAttribute("data-turbo", "false")
    link.textContent = strings.link
    link.addEventListener("click", () => {
      write("localStorage", DISMISSED)
      track("language-toast-switched")
    })

    const dismiss = document.createElement("button")
    dismiss.className = "lang-toast-dismiss"
    dismiss.type = "button"
    dismiss.setAttribute("aria-label", strings.close)
    dismiss.textContent = "✕"
    dismiss.addEventListener("click", () => {
      write("localStorage", DISMISSED)
      track("language-toast-dismissed")
      this.fadeOut()
    })

    toast.append(message, link, dismiss)
    document.body.appendChild(toast)
    this.toast = toast
    // A reflow between insertion and the class, so it slides in.
    void toast.offsetHeight
    toast.classList.add("lang-toast-visible")
    track("language-toast-shown")
  }

  // Picking a language from the switcher is a choice too.
  onSwitcherClick = (event) => {
    if (event.target.closest("a[hreflang]")) write("localStorage", DISMISSED)
  }

  fadeOut() {
    const toast = this.toast
    if (!toast) return
    this.toast = null
    toast.classList.remove("lang-toast-visible")
    setTimeout(() => toast.remove(), 300)
  }

  // Turbo takes its snapshot right after turbo:before-cache, so a fade would
  // leave a dead toast in it.
  removeNow = () => {
    if (!this.toast) return
    this.toast.remove()
    this.toast = null
  }
}
